import React from 'react';
import { Paper, Typography, Box, IconButton, styled, useTheme } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReceiptIcon from '@mui/icons-material/Receipt';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from 'react-router-dom';
import { get } from '../../utility/fetch';
import { get as gets } from '../../utility/fetchWeb';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useEffect } from 'react';

const WidgetPaper = styled(Paper)(({ theme, backgroundColor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexGrow: 1,
  padding: theme.spacing(2), // Adjusted padding for smaller screens
  borderRadius: theme.shape.borderRadius,
  backgroundColor: backgroundColor || '#E0F2F7', // Light blue-grey
}));

const IconContainer = styled(IconButton)(({ theme }) => ({
  backgroundColor: '#FFCA28', // Amber
  color: theme.palette.common.white,
  marginRight: theme.spacing(1), // Adjusted margin for smaller screens
}));

const TitleTypography = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  color: 'black', // Darker amber
  fontSize: theme.typography.fontSize * 0.9, // Adjusted font size for smaller screens
}));

const SubtitleTypography = styled(Typography)(({ theme }) => ({
  color: 'black', // Slightly darker amber
  fontSize: theme.typography.fontSize * 0.8, // Adjusted font size for smaller screens
}));

const BadgeBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#9C27B0', // Purple
  color: theme.palette.common.white,
  paddingLeft: theme.spacing(1), // Adjusted padding for smaller screens
  paddingRight: theme.spacing(1), // Adjusted padding for smaller screens
  borderRadius: theme.shape.borderRadius,
  minWidth: theme.spacing(4), // Adjusted min width for smaller screens
  height: theme.spacing(2.5), // Adjusted height for smaller screens
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: theme.typography.fontSize * 0.7, // Adjusted font size for smaller screens
}));

const ForwardIcon = styled(ArrowForwardIosIcon)(({ theme }) => ({
  color: '#FBC02D', // Slightly darker amber
  fontSize: theme.typography.fontSize * 0.8, // Adjusted font size for smaller screens
}));


const TopBarWidget = ({ icon, title, subtitle, badge, backgroundColor, onClick }) => (
  <WidgetPaper
    backgroundColor={backgroundColor}
    onClick={onClick}
    sx={{ cursor: onClick ? 'pointer' : 'default' }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
      <IconContainer size="small">
        {icon}
      </IconContainer>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TitleTypography variant="subtitle2" noWrap>
          {title}
        </TitleTypography>
        <SubtitleTypography variant="caption" noWrap>
          {subtitle}
        </SubtitleTypography>
      </Box>
    </Box>
    {badge && (
      <BadgeBox>
        {badge}
      </BadgeBox>
    )}
    {!badge && <ForwardIcon size="small" />}
  </WidgetPaper>
);

const TopBar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [receivedRequests, setReceivedRequests] = React.useState([]);
  const [appointments, setAppointments] = useState([]);
  const role = sessionStorage?.getItem("role");

  const isDoctor = role === "Doctor";

  const getReceivedRequests = async () => {
    try {
      const userId = sessionStorage.getItem('userId');
      const res = await gets(`/friendrequest/list/receiver-employee/${userId}`);
      if (res?.data) {
        setReceivedRequests(res.data);
      }
    } catch (error) {
      console.error('Error fetching received requests:', error);
    }
  };

  const getAppointments = async () => {
    try {
      let res = await get(`/Appointment/doctor/${sessionStorage?.getItem("userId")}`);
      if (res?.status === "success") {
        setAppointments(res?.data || []); // Store all appointments
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
    }
  };

  // Add useEffect to fetch data
  useEffect(() => {
    if (isDoctor) {
      getAppointments();
    }
    getReceivedRequests();
  }, [isDoctor]);

  // Simplified helper function to get upcoming appointments count
  const getUpcomingAppointmentsCount = () => {
    const today = dayjs();
    return appointments.filter(appointment =>
      dayjs(appointment.appointDate).isAfter(today)
    ).length;
  };

  // Update the return section
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
        flexDirection: { xs: 'column', sm: 'row' },
      }}
    >
      {isDoctor && (
        <TopBarWidget
          icon={<CalendarTodayIcon />}
          title="Appointment"
          subtitle={`${getUpcomingAppointmentsCount()} Upcoming`}
          backgroundColor={'#FAE9E9'}
          onClick={() => navigate('/appointments')}
        />
      )}
      <TopBarWidget
        icon={<GroupAddIcon />}
        title="Connection Request"
        subtitle={`${receivedRequests.filter(req => req.status === 0).length} people waiting to connect`}
        badge={receivedRequests.filter(req => req.status === 0).length || undefined}
        backgroundColor={'#EEDBFA'}
        onClick={() => navigate('/socials', {
          state: { activeTab: 4 },
          replace: true // This prevents adding unnecessary entries to history
        })}
      />
    </Box>
  );
};

export default TopBar;